import { DefaultConfigModel, GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth } from '../../models';
import { CONFIG_VERSION } from './config.constants';

/**
 * The complete set of defaults. Unlike a customer config, every section it declares is always
 * present, so consumers can read `DEFAULT_CONFIG.header` etc. without narrowing.
 */
export const DEFAULT_CONFIG: DefaultConfigModel = {
  version: CONFIG_VERSION,
  pollingInterval: 5,
  initPollingInterval: 60,
  seo: {
    googleSiteVerification: ''
  },
  header: {
    sticky: true,
    showShadow: true,
    layout: 'Layout',
    template: 'basic',
    backgroundColor: 'light-shine',
    padding: {
      default: { top: 12, right: 20, bottom: 12, left: 20 },
      lg: { top: 12, right: 40, bottom: 12, left: 40 },
      xl: { top: 12, right: 112, bottom: 12, left: 112 }
    },
    topbar: {
      active: true,
      sticky: true,
      template: 'openingHours',
      divider: false,
      backgroundColor: 'light-shade',
      textColor: 'light-contrast',
      font: {
        family: 'source-sans-3',
        style: 'normal',
        weight: 300,
        textTransform: 'none'
      },
      textSize: 14
    },
    mainbar: {
      active: true,
      actions: {
        iconColor: 'secondary-main',
        iconColorHover: 'secondary-shade',
        gap: 48,
        showIconInSidebar: false,
        actions: {
          login: {
            active: { default: true },
            icon: {
              name: 'user',
              weight: 'light'
            },
            orderIndex: 20
          },
          shoppingcart: {
            active: { default: true },
            icon: {
              name: 'basket',
              weight: 'light'
            },
            orderIndex: 40
          },
          inquiry_shoppingcart: {
            active: { default: true },
            icon: {
              name: 'list-checks',
              weight: 'light'
            },
            orderIndex: 35
          },
          dashboard: {
            active: { default: true },
            icon: {
              name: 'user',
              weight: 'light'
            },
            orderIndex: 50
          },
          logout: {
            active: { default: true },
            icon: {
              name: 'sign-out',
              weight: 'light'
            },
            orderIndex: 10
          },
          watchlist: {
            active: { default: true },
            icon: {
              name: 'heart',
              weight: 'light'
            },
            orderIndex: 30
          }
        }
      },
      search: {
        active: true,
        icon: {
          name: 'magnifying-glass',
          weight: 'light'
        }
      },
      logo: {
        active: true,
        useHomeLinkTarget: true,
        useFullHeight: false
      },
      myChainstore: {
        active: true,
        displayMode: 'both',
        icon: {
          name: 'map-pin',
          weight: 'light'
        }
      }
    },
    menubar: {
      alignment: 'left',
      backgroundColor: 'light-main',
      textColor: 'dark-main',
      activeTextColor: 'secondary-main',
      activeBackgroundColor: 'light-main',
      activeUnderlined: false,
      font: {
        family: 'source-sans-3',
        style: 'normal',
        weight: 400,
        textTransform: 'none'
      },
      textSize: 18,
      verticalPadding: 12,
      gapBetweenItems: 16,
      showOpeningHoursInSidebar: true,
      dropdownMenu: {
        textSize: 16,
        font: {
          family: 'source-sans-3',
          style: 'normal',
          weight: 400,
          textTransform: 'none'
        },
        textColor: 'dark-main'
      },
      burgerButton: {
        icon: {
          name: 'list',
          weight: 'light',
          animation: 'none'
        },
        active: true
      }
    },
    productItems: {
      showWatchlistButton: false,
      showShoppingCartButton: false
    }
  },
  footer: {
    template: 'basic',
    showShadow: true,
    backgroundColor: 'light-shade',
    padding: {
      default: { top: 0, right: 0, bottom: 0, left: 0 },
      lg: { top: 0, right: 40, bottom: 0, left: 40 },
      xl: { top: 0, right: 112, bottom: 0, left: 112 }
    },
    layout: 'LayoutContent',
    blockOrder: {
      paymentMethods: 10,
      deliveryMethods: 20,
      socialMediaLinks: 30,
      appLinks: 40,
      revocation: 50
    },
    infoarea: {
      appLinks: {
        active: true
      },
      chainstore: {
        active: true,
        showAddress: true,
        showEmail: true,
        showPhoneNumber: true,
        showOpeningHours: true,
        showSpecialOpeningHours: true,
        showCompanyName: false,
        showAllChainstoresLink: true
      },
      deliveryMethods: {
        active: true
      },
      logo: {
        active: true,
        useHomeLinkTarget: true
      },
      socialMediaLinks: {
        active: true,
        color: 'dark-main'
      },
      paymentMethods: {
        active: true
      },
      revocation: {
        active: true
      },
      text: {
        color: 'dark-main',
        font: {
          family: 'source-sans-3',
          style: 'normal',
          weight: 300,
          textTransform: 'none'
        },
        textSize: { default: 18, lg: 22 }
      }
    },
    menuarea: {
      gapBetweenItems: 16,
      columns: 3,
      heading: {
        color: 'dark-main',
        font: {
          family: 'cormorant',
          style: 'normal',
          weight: 500,
          textTransform: 'none'
        },
        textSize: { default: 24, lg: 28 }
      },
      items: {
        color: 'dark-main',
        font: {
          family: 'source-sans-3',
          style: 'normal',
          weight: 300,
          textTransform: 'none'
        },
        textSize: { default: 18, lg: 22 }
      }
    },
    bottombar: {
      active: true,
      backgroundColor: 'primary-main',
      text: {
        color: 'primary-contrast',
        font: {
          family: 'source-sans-3',
          style: 'normal',
          weight: 300,
          textTransform: 'none'
        },
        textSize: { default: 18, lg: 22 }
      }
    }
  },
  colors: {
    primary: {
      main: '#435b60',
      shine: '#697c80',
      shade: '#36494d',
      contrast: '#ffffff'
    },
    secondary: {
      main: '#67a2be',
      shine: '#85b5cb',
      shade: '#528298',
      contrast: '#ffffff'
    },
    tertiary: {
      main: '#69598f',
      shine: '#877aa5',
      shade: '#544772',
      contrast: '#ffffff'
    },
    light: {
      main: '#ffffff',
      shine: '#ffffff',
      shade: '#cccccc',
      contrast: '#000000'
    },
    medium: {
      main: '#cdcbc5',
      shine: '#ededed',
      shade: '#a4a29e',
      contrast: '#000000'
    },
    dark: {
      main: '#000000',
      shine: '#333333',
      shade: '#000000',
      contrast: '#ffffff'
    },
    success: {
      main: '#55af64',
      shine: '#77bf83',
      shade: '#448c50',
      contrast: '#ffffff'
    },
    warning: {
      main: '#e2b25a',
      shine: '#e8c17b',
      shade: '#b58e48',
      contrast: '#ffffff'
    },
    danger: {
      main: '#d84b59',
      shine: '#e06f7a',
      shade: '#ad3c47',
      contrast: '#ffffff'
    }
  },
  uiElements: {
    buttons: {
      backgroundColor: 'secondary-main',
      hoverBackgroundColor: 'secondary-shade',
      disabledBackgroundColor: 'medium-shine',
      textColor: 'secondary-contrast',
      hoverTextColor: 'secondary-contrast',
      disabledTextColor: 'medium-contrast',
      borderWidth: GsStructBorderWidth.zeroPixel,
      borderStyle: GsStructBorderStyle.none,
      borderColor: 'secondary-main',
      borderHoverColor: 'secondary-shade',
      borderDisabledColor: 'medium-shine',
      borderRadius: GsStructBorderRadius.none,
      innerPadding: {
        default: { top: 13, right: 40, bottom: 13, left: 40 },
        lg: { top: 15, right: 40, bottom: 15, left: 40 }
      },
      font: {
        family: 'source-sans-3',
        weight: 400,
        style: 'normal',
        textTransform: 'none'
      },
      textSize: { default: 16, lg: 22 },
      textCaps: false
    }
  },
  tagStyles: {
    h1: {
      default: {
        size: 2.25,
        weight: 700,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    h2: {
      default: {
        size: 1.875,
        weight: 600,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    h3: {
      default: {
        size: 1.5,
        weight: 600,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    h4: {
      default: {
        size: 1.25,
        weight: 600,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    h5: {
      default: {
        size: 1.125,
        weight: 600,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    h6: {
      default: {
        size: 1,
        weight: 600,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    },
    p: {
      default: {
        size: 1,
        weight: 500,
        family: 'arial',
        style: 'normal',
        textTransform: 'none'
      }
    }
  }
};
